(ns capra.core
  "Client-side public API for capra.

   Two calling modes:
     pipe!  — explicit pipeline, one round trip guaranteed
     call   — works in both modes (recording inside pipe!, microbatch outside)"
  (:require
   [capra.context :as ctx]
   [capra.pipeline :as pipeline]
   [capra.stub :as stub]
   [capra.callback :as callback]
   [capra.lifecycle :as lifecycle]
   [capra.microbatch :as microbatch]
   [capra.transport :as transport]
   [capra.readers :as readers]
   [capra.data :as data]
   [capra.protocol :as proto]
   [clojure.walk :as walk]))

;; =============================================================================
;; Client Record
;; =============================================================================

(defrecord CapraClient [transport root-stub microbatch-ctx])

(declare call)

;; =============================================================================
;; Recording Context (replaces pipe! callback pattern)
;; =============================================================================

(defrecord RecordingCtx [client ops-atom next-id-atom])

(defn ctx
  "Create a reusable recording context from a client.
   Use with capra/call to record ops, then capra/await to flush."
  [client]
  (->RecordingCtx client (atom []) (atom -1)))

;; =============================================================================
;; Public API
;; =============================================================================

(defn install-readers!
  "Install #rpc/* tag literal readers. Call once at app startup."
  []
  (readers/install!))

(defn make-client
  "Create a capra client.

   transport: ICapraTransport implementation (e.g. Sente adapter)"
  [transport]
  (let [mb-ctx (microbatch/make-microbatch-ctx)
        client (->CapraClient transport (atom nil) mb-ctx)]
    ;; Wire incoming push messages to callback dispatcher
    (transport/on-push! transport callback/dispatch-invocation!)

    ;; Wire welcome to capture root stub
    (transport/on-welcome! transport
                           (fn [{:keys [session-cap caps]}]
                             (let [root (stub/cap->stub
                                         (merge {:id (:id session-cap)}
                                                (first caps)))]
                               (reset! (:root-stub client) root)
                               (lifecycle/register-stub! root))))

    ;; Wire reconnect
    (transport/on-reconnect! transport
                             (fn []
                               ;; Clear stale state
                               (lifecycle/clear-all!)
                               (callback/clear-all!)
                               (microbatch/reset-state! mb-ctx)))

    ;; Initialize data convenience module's call-fn
    (data/set-call-fn! (fn [ctx target method args]
                         (call ctx target method args)))

    client))

(defn root
  "Get the root capability stub for this client."
  [client]
  @(:root-stub client))

(defn call
  "Issue an RPC call.

   Inside pipe! (ctx is a PipelineCtx):
     Records the op, returns a PendingRef.

   Outside pipe! (ctx is a CapraClient):
     Enqueues in the implicit microbatch, returns a Promise.

   target: :root, a CapraStub, a PendingRef, or a Ref (from ref-path)
   method: keyword (e.g. :job/list, :data/slice)
   args:   map of arguments"
  [ctx target method args]
  (cond
    ;; RecordingCtx — record op, return PendingRef
    (instance? RecordingCtx ctx)
    (ctx/record-op! ctx target method args)

    (ctx/pipeline-ctx? ctx)
    (ctx/record-op! ctx target method args)

    (instance? CapraClient ctx)
    (let [resolved-target (if (= :root target)
                            (root ctx)
                            target)
          send-fn! (fn [msg]
                     (transport/send-pipeline! (:transport ctx) msg))]
      (microbatch/enqueue-call! (:microbatch-ctx ctx) send-fn! resolved-target method args))

    :else
    (throw (ex-info "call: first arg must be a RecordingCtx, PipelineCtx, or CapraClient"
                    {:got (type ctx)}))))

(defn pipe!
  "Execute a pipeline in a single round trip.

   user-fn receives a PipelineCtx. Inside it, use capra/call to record ops.
   The return value determines which results are resolved.

   Returns Promise<return-value-with-resolved-data>.

   Example:
     (pipe! client
       (fn [ctx]
         (let [api  (call ctx :root :auth/login {:token token})
               jobs (call ctx api :job/list {:status :active})]
           jobs)))
     ;; => Promise<jobs-data>"
  [client user-fn]
  (let [{:keys [ops return-val]} (pipeline/build-pipeline user-fn)
        return-refs              (pipeline/extract-return-refs return-val)
        qid                      (microbatch/next-qid (:microbatch-ctx client))
        payload                  {:type   :rpc/call
                                  :qid    qid
                                  :ops    ops
                                  :return return-refs}]
    (-> (transport/send-pipeline! (:transport client) payload)
        (.then (fn [response]
                 (let [resolved (pipeline/resolve-results response return-val)
                       root-stub (root client)]
                   ;; Register any new stubs as children of root
                   (when root-stub
                     (walk/postwalk
                      (fn [x]
                        (when (and (stub/stub? x)
                                   (not= (:cap-id x) (:cap-id root-stub)))
                          (lifecycle/register-child! root-stub x))
                        x)
                      resolved))
                   resolved))))))

(defn callback
  "Create a callback that the server can invoke via :rpc/push.
   Returns a CapraCallback — serializes to #rpc/fn in pipeline args.

   Options:
     :owned-by  CapraStub — callback is released when stub is disposed"
  ([local-fn]
   (callback local-fn nil))
  ([local-fn {:keys [owned-by]}]
   (let [cb (callback/make-callback local-fn)]
     (when owned-by
       (lifecycle/own-callback! owned-by cb))
     cb)))

(defn ref-path
  "Create a ref that navigates into an op result.
   Use inside pipe! to access nested values.

   (let [job (call ctx api :job/get {:id \"abc\"})
         quotes (call ctx (ref-path job :quotes) :data/count {})]
     quotes)"
  [pending-ref & path]
  (apply ctx/ref-path pending-ref path))

(defn await
  "Flush recorded ops as a single round trip. Returns a Promise.
   Single ref → Promise<value>. Multiple refs → Promise<[v1 v2 ...]>.
   Rejects with ex-info if any requested ref has a pipeline error.
   Resets ctx for reuse after flushing."
  [recording-ctx & refs]
  (let [ops     @(:ops-atom recording-ctx)
        ref-ids (into #{} (map :op-id) (filter ctx/pending-ref? refs))
        single? (= 1 (count refs))
        qid     (microbatch/next-qid (-> recording-ctx :client :microbatch-ctx))
        payload {:type   :rpc/call
                 :qid    qid
                 :ops    ops
                 :return ref-ids}]
    ;; Reset for reuse
    (reset! (:ops-atom recording-ctx) [])
    (reset! (:next-id-atom recording-ctx) -1)
    (-> (transport/send-pipeline! (-> recording-ctx :client :transport) payload)
        (.then (fn [response]
                 (let [error-ops (:error-ops response)
                       ref-errors (when (seq error-ops)
                                    (keep (fn [ref]
                                            (get error-ops (:op-id ref)))
                                          refs))]
                   (if (seq ref-errors)
                     (let [err (first ref-errors)]
                       (throw (ex-info (or (:message err) "RPC error")
                                       {:code (:code err)
                                        :error-ops error-ops})))
                     (let [results  (:results response)
                           resolved (mapv (fn [ref]
                                            (get results (:op-id ref)))
                                          refs)]
                       (if single? (first resolved) resolved)))))))))

(defn dispose!
  "Release a capability stub. Sends :rpc/release to server,
   drops owned callbacks and child stubs."
  [client stub-or-stubs]
  (let [stubs (if (stub/stub? stub-or-stubs) [stub-or-stubs] stub-or-stubs)]
    (doseq [s stubs]
      (lifecycle/dispose!
       s
       (fn [cap-ids fn-ids]
         (transport/send-release! (:transport client) cap-ids fn-ids))))))
